/**
 * ALQUIMIA CHARMS — Sistema de reseñas y testimonios en video
 * - <alq-video-carousel>: carrusel tipo TikTok/Reels para homepage (reutiliza MinimogLibs.Swiper,
 *   ya cargado globalmente por el tema vía vendor.js — no se agrega ninguna librería nueva).
 * - <alq-reviews-grid>: muro de reseñas con orden/filtro client-side y expandir texto.
 * Ningún video se reproduce automáticamente: el <video> no tiene src hasta que la persona
 * toca "play" (lazy load real, no solo atributo loading="lazy").
 */
(function () {
  "use strict";

  // Un solo video reproduciéndose a la vez en toda la página (evita ruido/competir por audio)
  let alqCurrentlyPlaying = null;

  function alqStopVideo(card) {
    if (!card) return;
    const video = card.querySelector("video");
    if (video && !video.paused) {
      video.pause();
    }
    card.classList.remove("is-playing");
  }

  function alqPlayVideo(card) {
    if (!card || !card.classList.contains("has-video")) return;
    const video = card.querySelector("video");
    if (!video) return;

    if (alqCurrentlyPlaying && alqCurrentlyPlaying !== card) {
      alqStopVideo(alqCurrentlyPlaying);
    }

    // Lazy load real: solo se setea el src del <source> la primera vez que se reproduce
    const source = video.querySelector("source[data-src]");
    if (source) {
      source.src = source.getAttribute("data-src");
      source.removeAttribute("data-src");
      video.load();
    }

    video.play().catch(function () {
      /* autoplay bloqueado por el navegador o el usuario canceló: no pasa nada */
    });
    card.classList.add("is-playing");
    alqCurrentlyPlaying = card;
  }

  function alqBindCardPlayback(root) {
    root.querySelectorAll("[data-alq-play]").forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        const card = btn.closest("[data-alq-video-card]");
        alqPlayVideo(card);
      });
    });
    root.querySelectorAll("[data-alq-video-card].has-video").forEach(function (card) {
      const video = card.querySelector("video");
      if (!video) return;
      video.addEventListener("ended", function () {
        card.classList.remove("is-playing");
      });
    });
  }

  // Pausa el video activo cuando su tarjeta sale del viewport (scroll o cambio de slide)
  function alqObserveViewport(root) {
    if (!("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            const card = entry.target;
            if (card.classList.contains("is-playing")) {
              alqStopVideo(card);
            }
          }
        });
      },
      { threshold: 0.15 }
    );
    root.querySelectorAll("[data-alq-video-card].has-video").forEach(function (card) {
      io.observe(card);
    });
  }

  /* ------------------------------------------------------------------ */
  /* <alq-video-carousel>                                                */
  /* ------------------------------------------------------------------ */
  if (!customElements.get("alq-video-carousel")) {
    class AlqVideoCarousel extends HTMLElement {
      connectedCallback() {
        alqBindCardPlayback(this);
        alqObserveViewport(this);

        this.sliderContainer = this.querySelector(".alq-vt__swiper");
        if (!this.sliderContainer || this.dataset.total === "0") return;

        const init = () => {
          if (!(window.MinimogLibs && window.MinimogLibs.Swiper)) {
            // El bundle vendor.js del tema aún no terminó de cargar: reintenta.
            return setTimeout(init, 60);
          }

          this.slider = new window.MinimogLibs.Swiper(this.sliderContainer, {
            slidesPerView: 1.35,
            spaceBetween: 12,
            watchOverflow: true,
            navigation: {
              nextEl: this.querySelector(".alq-vt__nav-next"),
              prevEl: this.querySelector(".alq-vt__nav-prev"),
            },
            breakpoints: {
              560: { slidesPerView: 2.2, spaceBetween: 14 },
              768: { slidesPerView: 3.2, spaceBetween: 16 },
              1000: { slidesPerView: 4, spaceBetween: 18 },
              1280: { slidesPerView: 5, spaceBetween: 20 },
            },
            on: {
              slideChangeTransitionStart: () => {
                // Pausa cualquier video que haya quedado reproduciéndose al cambiar de slide
                if (alqCurrentlyPlaying && this.contains(alqCurrentlyPlaying)) {
                  alqStopVideo(alqCurrentlyPlaying);
                }
              },
            },
          });
        };

        init();
      }
    }
    customElements.define("alq-video-carousel", AlqVideoCarousel);
  }

  /* ------------------------------------------------------------------ */
  /* <alq-reviews-grid>                                                  */
  /* ------------------------------------------------------------------ */
  if (!customElements.get("alq-reviews-grid")) {
    class AlqReviewsGrid extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector("[data-alq-grid]");
        this.sortSelect = this.querySelector("[data-alq-sort]");
        this.filterSelect = this.querySelector("[data-alq-filter]");
        this.countEl = this.querySelector("[data-alq-visible-count]");
        this.loadMoreBtn = this.querySelector("[data-alq-loadmore]");
        this.pageSize = parseInt(this.dataset.pageSize || "9", 10);
        this.visibleLimit = this.pageSize;

        alqBindCardPlayback(this);
        alqObserveViewport(this);
        this.bindExpandButtons();

        if (this.sortSelect) {
          this.sortSelect.addEventListener("change", () => this.apply());
        }
        if (this.filterSelect) {
          this.filterSelect.addEventListener("change", () => {
            this.visibleLimit = this.pageSize;
            this.apply();
          });
        }
        if (this.loadMoreBtn) {
          this.loadMoreBtn.addEventListener("click", () => {
            this.visibleLimit += this.pageSize;
            this.apply();
          });
        }

        this.apply();
      }

      bindExpandButtons() {
        this.querySelectorAll("[data-alq-expand]").forEach((btn) => {
          btn.addEventListener("click", () => {
            const text = btn
              .closest(".alq-review-card")
              .querySelector(".alq-review-card__text");
            const expanded = text.classList.toggle("is-expanded");
            btn.textContent = expanded
              ? btn.dataset.lessLabel
              : btn.dataset.moreLabel;
          });
        });
      }

      apply() {
        if (!this.grid) return;
        const cards = Array.from(this.grid.children);
        const filter = this.filterSelect ? this.filterSelect.value : "all";
        const sort = this.sortSelect ? this.sortSelect.value : "recent";

        let filtered = cards.filter((card) => {
          if (filter === "media") return card.dataset.hasMedia === "true";
          if (filter === "5" || filter === "4" || filter === "3" || filter === "2" || filter === "1") {
            return card.dataset.rating === filter;
          }
          return true;
        });

        filtered.sort((a, b) => {
          if (sort === "rating_desc") {
            return parseFloat(b.dataset.rating) - parseFloat(a.dataset.rating);
          }
          if (sort === "rating_asc") {
            return parseFloat(a.dataset.rating) - parseFloat(b.dataset.rating);
          }
          // recent (default): fecha descendente, luego orden manual
          const dateDiff = parseInt(b.dataset.date || "0", 10) - parseInt(a.dataset.date || "0", 10);
          if (dateDiff !== 0) return dateDiff;
          return parseInt(a.dataset.order || "0", 10) - parseInt(b.dataset.order || "0", 10);
        });

        // Reordena en el DOM
        filtered.forEach((card) => this.grid.appendChild(card));
        cards
          .filter((c) => !filtered.includes(c))
          .forEach((card) => this.grid.appendChild(card));

        // Muestra/oculta según el límite visible actual
        filtered.forEach((card, i) => {
          card.hidden = i >= this.visibleLimit;
        });
        cards.filter((c) => !filtered.includes(c)).forEach((card) => {
          card.hidden = true;
        });

        if (this.countEl) {
          const shown = Math.min(this.visibleLimit, filtered.length);
          this.countEl.textContent = this.countEl.dataset.template
            .replace("{shown}", shown)
            .replace("{total}", filtered.length);
        }
        if (this.loadMoreBtn) {
          this.loadMoreBtn.hidden = this.visibleLimit >= filtered.length;
        }

        this.classList.toggle("is-empty", filtered.length === 0);
      }
    }
    customElements.define("alq-reviews-grid", AlqReviewsGrid);
  }
})();
